import React from 'react';
import { useRouter } from 'next/router';
import { gql } from '@soundxyz/gql-string';
import { Button } from '@components/main-components/button/Button';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { ArtistApplicationStatus, LearnMoreFooterDocument, useQuery } from '@graphql/reactQuery';
import { useAuthArtistApplicationStatus } from '@hooks/useAuthArtistApplicationStatus';
import { trackClickEvent } from '@lib/analytics';
import { NAV_LINKS } from '@utils/constants';
import type { SharedProps } from '../index';
import { ArtistHighlight } from './ArtistHighlight';

/*#__PURE__*/ gql(/* GraphQL */ `
  query LearnMoreFooter {
    artistPartnership {
      id
      message
      ...ArtistHighlights
    }
  }
`);

export const LearnMore = ({ learnMore }: SharedProps) => {
  const { authUser, isConnectingAPIUser } = useAuth();
  const {
    hasApplied,
    loading: applicationStatusCheckPending,
    status,
  } = useAuthArtistApplicationStatus();
  const { pathname } = useRouter();

  const { data } = useQuery(LearnMoreFooterDocument, {
    staleTime: Infinity,
    cacheTime: Infinity,
    initialData: learnMore,
  });

  if (!data) {
    return null;
  }
  const artistPartnership = data.data.artistPartnership;
  const hasArtistRole = !!authUser?.artist;

  const applied = hasApplied && !applicationStatusCheckPending;
  const rejectedApplication = applied && status === ArtistApplicationStatus.Rejected;
  const pendingApplication = applied && status === ArtistApplicationStatus.Pending;

  const showJoinAsArtistButton = (!hasArtistRole || rejectedApplication) && !pendingApplication;
  const showLearnMore = pathname !== NAV_LINKS.About;

  const joinAsAnArtistButton =
    !!authUser && !isConnectingAPIUser ? (
      <Button
        href={NAV_LINKS.ArtistApplication}
        label="Join as an artist"
        variant="tertiary"
        size="M"
        className="min-w-[128px] p-3"
        onClick={() => {
          trackClickEvent({
            event: 'Join as an Artist Clicked',
            properties: {
              sourceComponent: 'Landing Page',
            },
          });
        }}
      />
    ) : (
      <Button
        onClick={() => {
          trackClickEvent({
            event: 'Sign in Clicked',
            properties: { sourceComponent: 'Join as an Artist' },
          });
          triggerLogin();
        }}
        label="Join as an artist"
        variant="tertiary"
        size="M"
        className="min-w-[128px] p-3"
        loading={isConnectingAPIUser}
      />
    );

  return (
    <div
      className="relative flex w-full items-center justify-start overflow-hidden bg-[radial-gradient(75%_300%_at_75%_100%,_#EE5177_0%,_#E24784_12.5%,_#AF2D84_35%,_#0F0F0F_65%)] bg-cover bg-left-bottom
      bg-no-repeat px-4 py-8 text-white 
      md:rounded-lg md:px-8 md:pb-[71px] md:pt-[56px]  xl:justify-center xl:overflow-visible"
    >
      <div className="flex w-full max-w-[227px] flex-col gap-y-4  md:max-w-none lg:max-w-content">
        <h1 className="font-title text-title-m font-medium md:text-title-l ">
          Shape the future of the music industry
        </h1>
        <p className="font-base text-base-s opacity-80 md:max-w-[350px] md:text-base-l lg:max-w-[424px]">
          {artistPartnership.message}
          {showJoinAsArtistButton && showLearnMore && (
            <>
              .&nbsp;
              <AccessibleLink absoluteUri={NAV_LINKS.About} className=" underline" type="external">
                Learn more
              </AccessibleLink>
              .
            </>
          )}
        </p>
        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center">
          <div className="w-fit [&>a>button]:px-[10px] [&>a>button]:py-[12px]">
            {showJoinAsArtistButton
              ? joinAsAnArtistButton
              : showLearnMore && (
                  <Button
                    href={NAV_LINKS.About}
                    label="Learn more"
                    variant="tertiary"
                    size="M"
                    className="min-w-[unset]"
                  />
                )}
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-1 left-[60%] w-fit min-w-[380px] max-w-sm  flex-shrink-0 
         sm:-right-[8%] sm:bottom-auto sm:min-w-fit sm:max-w-md 
         md:-right-[25%] md:bottom-4 md:left-auto md:max-w-md
         lg:-right-32 lg:bottom-4 lg:max-w-xl  lt:-right-48
         xl:bottom-6 xl:right-1 xl:max-w-[41rem] 2xl:bottom-4 2xl:right-6"
      >
        <ArtistHighlight highlightFrag={artistPartnership} />
      </div>
    </div>
  );
};
